import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Table, Spinner, Alert, Form, Col, Row, Button, Modal, Card, Badge } from "react-bootstrap";
import { setJobId } from "../../redux/slices/jobIdSlice";
import { useNavigate } from "react-router-dom";
import { formatDateLocalEnhanced } from '../../utils/dateUtils';
import './MechanicalReview.css';

function MechanicalReview({ 
  jobs = [], 
  loading = false, 
  error = null, 
  onJobCompletion
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local state for UI functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'MechanicalReviewDate', direction: 'ascending' });
  const [hideShipped, setHideShipped] = useState(true); // Default to hiding shipped jobs
  const [dueDateFilter, setDueDateFilter] = useState(14); // Default to 2 weeks (14 days)
  const [hideCompletedMechanical, setHideCompletedMechanical] = useState(true);
  const [upcomingViewMode, setUpcomingViewMode] = useState('table'); // 'table' or 'calendar' - only for upcoming section
  const [showMechanicalDue, setShowMechanicalDue] = useState(true); // Toggle for Mechanical Due dates in calendar
  const [showReviewDue, setShowReviewDue] = useState(true); // Toggle for Review Due dates in calendar

  // Local state for job updates to reflect changes immediately
  const [localJobUpdates, setLocalJobUpdates] = useState({});
  // State for the completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [completionComment, setCompletionComment] = useState("");
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [completionError, setCompletionError] = useState(null);
  const [completionSuccess, setCompletionSuccess] = useState(false);

  // State for the return for revisions modal
  const [showRevisionsModal, setShowRevisionsModal] = useState(false);
  const [selectedJobForRevisions, setSelectedJobForRevisions] = useState(null);
  const [revisionsComment, setRevisionsComment] = useState("");
  const [savingRevisions, setSavingRevisions] = useState(false);
  const [revisionsError, setRevisionsError] = useState(null);
  const [revisionsSuccess, setRevisionsSuccess] = useState(false);
  const previousJobs = useRef(jobs);
  const activeEditor = showCompletionModal || showRevisionsModal || savingCompletion || savingRevisions;
  useRealtimeDraft(activeEditor);
  useEffect(() => {
    if (activeEditor || previousJobs.current === jobs) return;
    previousJobs.current = jobs;
    setLocalJobUpdates({});
  }, [jobs, activeEditor]);

  // Helper function to get updated job data
  const getUpdatedJobs = useCallback(() => {
    return jobs.map(job => ({
      ...job,
      ...localJobUpdates[job.ID]
    }));
  }, [jobs, localJobUpdates]);

  // Filter jobs to only show those with MechanicalDone = "R"
  const processJobs = useCallback(() => {
    const updatedJobs = getUpdatedJobs();
    if (!updatedJobs || updatedJobs.length === 0) return [];

    // Filter for jobs with MechanicalDone = "R"
    let filteredData = updatedJobs.filter(job => job.MechanicalDone === "R");
    
    // Filter out shipped jobs if hideShipped is true
    if (hideShipped) {
      filteredData = filteredData.filter(job => job.JobStatus !== "Shipped");
    }
    
    return filteredData;
  }, [getUpdatedJobs, hideShipped]);

  const reviewJobs = processJobs();

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedJobs = [...reviewJobs].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;
    
    if (aValue < bValue) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const filteredJobs = sortedJobs.filter((job) => {
    // Apply search term filter across JobNumber, ProjectName, and ModelNumber
    const matchesSearch = searchTerm
      ? (job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         job.ModelNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;
    
    return matchesSearch;
  });
  const formatDate = (dateString) => {
    return formatDateLocalEnhanced(dateString);
  };

  const handleRowClick = (job) => {
    if (!canOpenPortalJob(job)) return;
    const jobId = job.ID;
    dispatch(setJobId(jobId));
    navigate(`/${jobId}/generalInfo`);
  };
  // Handle opening the completion modal
  const handleMarkAsComplete = (e, job) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedJob(job);
    setCompletionComment(job.MechanicalReviewComments || "");
    setShowCompletionModal(true);
  };
  // Handle closing the completion modal
  const handleCloseCompletionModal = () => {
    setShowCompletionModal(false);
    setSelectedJob(null);
    setCompletionComment("");
    setCompletionError(null);
    setCompletionSuccess(false);
  };

  // Handle confirming the completion
  const handleConfirmCompletion = async () => {
    if (!selectedJob) return;
    
    setSavingCompletion(true);
    setCompletionError(null);
    
    try {
      if (onJobCompletion) {
        // Get today's date in local time, not UTC
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const localDateString = `${year}-${month}-${day}`;
          // Update MechanicalDone to current date
        await onJobCompletion(selectedJob.ID, {
          MechanicalDone: localDateString,
          MechanicalReviewComments: completionComment
        });
        
        // Update local state to reflect the change immediately
        setLocalJobUpdates(prev => ({
          ...prev,
          [selectedJob.ID]: {
            ...prev[selectedJob.ID],
            MechanicalDone: localDateString,
            MechanicalReviewComments: completionComment
          }
        }));
      }
      
      // Set success message
      setCompletionSuccess(true);
      
      // Close the modal after a brief delay to show success message
      setTimeout(() => {
        handleCloseCompletionModal();
      }, 1500);
      
    } catch (error) {
      console.error("Error marking job as complete:", error);
      setCompletionError("Failed to mark job as complete. Please try again.");
    } finally {
      setSavingCompletion(false);
    }
  };

  // Handle opening the return for revisions modal
  const handleReturnForRevisions = (e, job) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedJobForRevisions(job);
    setRevisionsComment(job.MechanicalReviewComments || "");
    setShowRevisionsModal(true);
  };

  // Handle closing the return for revisions modal
  const handleCloseRevisionsModal = () => {
    setShowRevisionsModal(false);
    setSelectedJobForRevisions(null);
    setRevisionsComment("");
    setRevisionsError(null);
    setRevisionsSuccess(false);
  };

  // Handle confirming the return for revisions
  const handleConfirmReturnForRevisions = async () => {
    if (!selectedJobForRevisions) return;
    
    setSavingRevisions(true);
    setRevisionsError(null);
      try {
      if (onJobCompletion) {
        // Get current MechanicalReviewCount and increment it
        const currentReviewCount = selectedJobForRevisions.MechanicalReviewCount;
        const newReviewCount = currentReviewCount === null ? 1 : currentReviewCount + 1;
        
        // Reset MechanicalDone and MechanicalReviewDate to null, update comment and increment count
        await onJobCompletion(selectedJobForRevisions.ID, {
          MechanicalDone: null,
          MechanicalReviewDate: null,
          MechanicalReviewComments: revisionsComment,
          MechanicalReviewCount: newReviewCount
        });
        
        // Update local state to reflect the change immediately
        setLocalJobUpdates(prev => ({
          ...prev,
          [selectedJobForRevisions.ID]: {
        ...prev[selectedJobForRevisions.ID],
        MechanicalDone: null,
        MechanicalReviewDate: null,
        MechanicalReviewComments: revisionsComment,
        MechanicalReviewCount: newReviewCount
          }
        }));
      }
      
      // Set success message
      setRevisionsSuccess(true);
      
      // Close the modal after a brief delay to show success message
      setTimeout(() => {
        handleCloseRevisionsModal();
      }, 1500);
      
    } catch (error) {
      console.error("Error returning job for revisions:", error);
      setRevisionsError("Failed to return job for revisions. Please try again.");
    } finally {
      setSavingRevisions(false);
    }
  };

  // Calculate Mechanical Due Date (MechanicalDue - 2 business days)
  const calculateMechanicalDueDate = useCallback((reviewDueDate) => {
    if (!reviewDueDate) return null;
    
    try {
      const [year, month, day] = reviewDueDate.split('-').map(num => parseInt(num, 10));
      const dueDate = new Date(year, month - 1, day);
      dueDate.setHours(0, 0, 0, 0);
      
      // Subtract 2 business days
      let daysToSubtract = 2;
      let currentDate = new Date(dueDate);
      
      while (daysToSubtract > 0) {
        currentDate.setDate(currentDate.getDate() - 1);
        const dayOfWeek = currentDate.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          daysToSubtract--;
        }
      }
      
      const resultYear = currentDate.getFullYear();
      const resultMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const resultDay = String(currentDate.getDate()).padStart(2, '0');
      
      return `${resultYear}-${resultMonth}-${resultDay}`;
    } catch (error) {
      console.error('Error calculating mechanical due date:', error);
      return null;
    }
  }, []);

  // Calculate days in review
  const getDaysInReview = (reviewDate) => {
    if (!reviewDate) return null;
    
    // Parse the date string as local time, not UTC
    const [year, month, day] = reviewDate.split('-').map(num => parseInt(num, 10));
    const review = new Date(year, month - 1, day); // month is 0-indexed
    review.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const diffTime = Math.abs(today - review);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Calculate days until due date
  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const [year, month, day] = dueDate.split('-').map(num => parseInt(num, 10));
    const due = new Date(year, month - 1, day);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Check if Mechanical Due Date is overdue (in the past)
  const isMechanicalDueDateOverdue = useCallback((mechanicalDueDate) => {
    if (!mechanicalDueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const [year, month, day] = mechanicalDueDate.split('-').map(num => parseInt(num, 10));
    const dueDate = new Date(year, month - 1, day);
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today;
  }, []);

  // Calculate days past due for Mechanical Due Date
  const getDaysPastDue = useCallback((mechanicalDueDate) => {
    if (!mechanicalDueDate) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const [year, month, day] = mechanicalDueDate.split('-').map(num => parseInt(num, 10));
    const dueDate = new Date(year, month - 1, day);
    dueDate.setHours(0, 0, 0, 0);
    
    if (dueDate >= today) return 0;
    
    const diffTime = today - dueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }, []);

  // Filter and sort jobs for upcoming mechanical due dates
  const getUpcomingMechanicalDueJobs = useCallback(() => {
    const updatedJobs = getUpdatedJobs();
    if (!updatedJobs || updatedJobs.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const filterEndDate = new Date(today);
    filterEndDate.setDate(filterEndDate.getDate() + dueDateFilter);

    // Filter jobs with MechanicalDue within selected period
    const upcomingJobs = updatedJobs.filter(job => {
      if (!job.MechanicalDue || job.MechanicalDue === 'R') return false;
      // MechanicalDone also holds status markers such as R (awaiting review).
      // Only a completion date removes the job from upcoming work.
      const completionDate = typeof job.MechanicalDone === 'string' ? job.MechanicalDone.trim() : '';
      if (hideCompletedMechanical && completionDate && !Number.isNaN(Date.parse(completionDate))) return false;
      
      const [year, month, day] = job.MechanicalDue.split('-').map(num => parseInt(num, 10));
      const dueDate = new Date(year, month - 1, day);
      dueDate.setHours(0, 0, 0, 0);
      
      return dueDate >= today && dueDate <= filterEndDate;
    });

    // Sort by soonest due date
    return upcomingJobs.sort((a, b) => {
      const [yearA, monthA, dayA] = a.MechanicalDue.split('-').map(num => parseInt(num, 10));
      const dateA = new Date(yearA, monthA - 1, dayA);
      
      const [yearB, monthB, dayB] = b.MechanicalDue.split('-').map(num => parseInt(num, 10));
      const dateB = new Date(yearB, monthB - 1, dayB);
      
      return dateA - dateB;
    });
  }, [getUpdatedJobs, dueDateFilter, hideCompletedMechanical]);

  const upcomingDueJobs = getUpcomingMechanicalDueJobs();

  // Get week range for calendar view
  const getWeekRange = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate days to Monday (start of week)
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() + daysToMonday);
    startOfWeek.setHours(0, 0, 0, 0);
    
    // Generate array of weeks based on dueDateFilter
    const weeks = [];
    const numWeeks = Math.ceil(dueDateFilter / 7);
    
    for (let weekIdx = 0; weekIdx < numWeeks; weekIdx++) {
      const weekStart = new Date(startOfWeek);
      weekStart.setDate(startOfWeek.getDate() + (weekIdx * 7));
      
      const weekDays = [];
      for (let dayIdx = 0; dayIdx < 5; dayIdx++) { // Monday to Friday
        const currentDay = new Date(weekStart);
        currentDay.setDate(weekStart.getDate() + dayIdx);
        weekDays.push(currentDay);
      }
      
      weeks.push(weekDays);
    }
    
    return weeks;
  }, [dueDateFilter]);

  // Helper function to format date key
  const getDateKey = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Group jobs by due date for calendar view - supports both Mechanical Due and Review Due
  const jobsByDate = useMemo(() => {
    const jobMap = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = getDateKey(today);
    
    upcomingDueJobs.forEach(job => {
      if (!job.MechanicalDue) return;
      
      const mechanicalDueDate = calculateMechanicalDueDate(job.MechanicalDue);
      const reviewDueDate = job.MechanicalDue;
      
      // Add job for Mechanical Due date
      if (showMechanicalDue && mechanicalDueDate) {
        const isOverdue = isMechanicalDueDateOverdue(mechanicalDueDate);
        const dateKey = isOverdue ? todayKey : mechanicalDueDate; // Show overdue on today
        
        if (!jobMap[dateKey]) {
          jobMap[dateKey] = [];
        }
        jobMap[dateKey].push({
          ...job,
          displayType: 'mechanical',
          isOverdue: isOverdue,
          daysPastDue: isOverdue ? getDaysPastDue(mechanicalDueDate) : 0,
          originalDueDate: mechanicalDueDate
        });
      }
      
      // Add job for Review Due date (only if not overdue on mechanical)
      if (showReviewDue && reviewDueDate) {
        const mechanicalIsOverdue = mechanicalDueDate && isMechanicalDueDateOverdue(mechanicalDueDate);
        if (!mechanicalIsOverdue) {
          if (!jobMap[reviewDueDate]) {
            jobMap[reviewDueDate] = [];
          }
          jobMap[reviewDueDate].push({
            ...job,
            displayType: 'review',
            isOverdue: false,
            daysPastDue: 0
          });
        }
      }
    });
    
    return jobMap;
  }, [upcomingDueJobs, showMechanicalDue, showReviewDue, calculateMechanicalDueDate, isMechanicalDueDateOverdue, getDaysPastDue, getDateKey]);

  // Render calendar view
  const renderCalendarView = () => {
    return (
      <div className="mechanical-calendar-view">
        {getWeekRange.map((week, weekIdx) => (
          <div key={weekIdx} className="calendar-week">
            <Row className="calendar-week-header">
              <Col>
                <h5>Week of {week[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</h5>
              </Col>
            </Row>
            <Row className="calendar-days">
              {week.map((day, dayIdx) => {
                const dateKey = getDateKey(day);
                const jobsForDay = jobsByDate[dateKey] || [];
                const isToday = getDateKey(new Date()) === dateKey;
                
                return (
                  <Col key={dayIdx} className="calendar-day">
                    <Card className={`day-card ${isToday ? 'today' : ''}`}>
                      <Card.Header className="day-header">
                        <div className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        <div className="day-date">{day.getDate()}</div>
                      </Card.Header>
                      {jobsForDay.length > 0 && (
                        <div className="job-count-container">
                          <Badge bg="primary" className="job-count">{jobsForDay.length} job{jobsForDay.length !== 1 ? 's' : ''}</Badge>
                        </div>
                      )}
                      <Card.Body className="day-jobs">
                        {jobsForDay.length === 0 ? (
                          <div className="no-jobs">No jobs due</div>
                        ) : (
                          jobsForDay.map((job, idx) => {
                            const isMechanicalType = job.displayType === 'mechanical';
                            
                            return (
                              <div 
                                key={`${job.ID}-${job.displayType}-${idx}`}
                                className={`job-item ${isMechanicalType ? 'mechanical-type' : 'review-type'} ${job.isOverdue ? 'overdue' : ''}`}
                                onClick={canOpenPortalJob(job) ? () => handleRowClick(job) : undefined}
                              >
                                <div className="job-type-indicator">
                                  <span className={`type-dot ${isMechanicalType ? 'mechanical-due-color' : 'review-due-color'}`}>●</span>
                                  <span className="type-label">{isMechanicalType ? 'Mechanical Due' : 'Review Due'}</span>
                                </div>
                                <div className="job-header">
                                  <span className="job-number">{job.JobNumber}</span>
                                  <span className="job-separator"> - </span>
                                  <span className="job-name">{job.ProjectName}</span>
                                </div>
                                <div className="job-model">{job.ModelNumber}</div>
                                <div className="job-engineer">{job.MechanicalEngineer}</div>
                                {job.isOverdue && (
                                  <Badge bg="danger" size="sm" className="mt-1">
                                    {job.daysPastDue} day{job.daysPastDue !== 1 ? 's' : ''} OVERDUE
                                  </Badge>
                                )}
                              </div>
                            );
                          })
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mechanical-review-container">
      {error && <Alert variant="danger">{error}</Alert>}
      
      <div className="mb-3 text-center">
        <h2>Mechanical Review</h2>
      </div>

      {/* Active Reviews Table - Always shown in table format */}
      <div className="mb-4">
            <h4>Active Reviews</h4>
            <Row className="mb-3">
              <Col md={10}>
                <Form.Control
                  type="text"
                  placeholder="Search by Job Number, Project Name, or Model Number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Check
                  type="switch"
                  id="hide-shipped-switch"
                  label="Hide Shipped Jobs"
                  checked={hideShipped}
                  onChange={() => setHideShipped(!hideShipped)}
                  className="mt-2"
                />
              </Col>
            </Row>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th onClick={() => handleSort('JobNumber')}>Job Number</th>
                  <th onClick={() => handleSort('ProjectName')}>Project Name</th>
                  <th onClick={() => handleSort('ModelNumber')}>Model Number</th>
                  <th onClick={() => handleSort('MechanicalEngineer')}>Mechanical Engineer</th>
                  <th onClick={() => handleSort('MechanicalReviewDate')}>Review Date</th>
                  <th>Days in Review</th>
                  <th onClick={() => handleSort('ProductionRelease')}>Release To Scheduling</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => {
                  const daysInReview = getDaysInReview(job.MechanicalReviewDate);
                  return (
                    <tr 
                      key={job.ID} 
                      onClick={canOpenPortalJob(job) ? () => handleRowClick(job) : undefined}
                      style={{ 
                        cursor: canOpenPortalJob(job) ? 'pointer' : 'default',
                        backgroundColor: daysInReview > 7 ? '#ffdddd' : 
                                         daysInReview > 3 ? '#ffffcc' : ''
                      }}
                    >
                      <td>{job.JobNumber}</td>
                      <td>{job.ProjectName}</td>
                      <td>{job.ModelNumber}</td>
                      <td>{job.MechanicalEngineer}</td>
                      <td>{formatDate(job.MechanicalReviewDate)}</td>
                      <td>
                        {daysInReview && (
                          <>
                            {daysInReview} day{daysInReview !== 1 ? 's' : ''}
                            {daysInReview > 7 && 
                              <span className="ms-2 badge bg-danger">Extended Review</span>
                            }
                            {daysInReview > 3 && daysInReview <= 7 && 
                              <span className="ms-2 badge bg-warning text-dark">Review Soon</span>
                            }
                          </>
                        )}
                      </td>
                      <td>{formatDate(job.ProductionRelease)}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <Button 
                            variant="success" 
                            size="sm" 
                            onClick={(e) => handleMarkAsComplete(e, job)}
                            className="w-100"
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Review Complete
                          </Button>
                          <Button 
                            variant="warning" 
                            size="sm" 
                            onClick={(e) => handleReturnForRevisions(e, job)}
                            className="w-100"
                          >
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            Return for Revisions
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {loading && <div className="text-center"><Spinner animation="border" /></div>}
            {!loading && filteredJobs.length === 0 && 
              <Alert variant="info">No jobs currently in mechanical review.</Alert>
            }
          </div>

      {/* Upcoming Due Dates Section */}
      <div className="mt-5">
        <div className="mb-3">
          <h4>Upcoming Mechanical Due Dates</h4>
          <Form.Check
            type="checkbox"
            id="hide-completed-mechanical"
            label="Hide completed mechanical jobs"
            checked={hideCompletedMechanical}
            onChange={event => setHideCompletedMechanical(event.target.checked)}
          />
          <div className="small text-muted">Hides jobs with a Mechanical Done date; jobs awaiting review remain visible.</div>
        </div>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Show jobs due within:</Form.Label>
              <Form.Select 
                value={dueDateFilter} 
                onChange={(e) => setDueDateFilter(parseInt(e.target.value, 10))}
              >
                <option value={7}>1 Week (7 days)</option>
                <option value={14}>2 Weeks (14 days)</option>
                <option value={21}>3 Weeks (21 days)</option>
                <option value={30}>1 Month (30 days)</option>
                <option value={60}>2 Months (60 days)</option>
                <option value={90}>3 Months (90 days)</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8} className="d-flex justify-content-end align-items-end gap-2">
            {upcomingViewMode === 'calendar' && (
              <div className="d-flex gap-2 me-3">
                <Form.Check
                  type="checkbox"
                  id="show-mechanical-due"
                  label={<span><span className="mechanical-due-color">●</span> Mechanical Due</span>}
                  checked={showMechanicalDue}
                  onChange={(e) => setShowMechanicalDue(e.target.checked)}
                  className="calendar-toggle"
                />
                <Form.Check
                  type="checkbox"
                  id="show-review-due"
                  label={<span><span className="review-due-color">●</span> Review Due</span>}
                  checked={showReviewDue}
                  onChange={(e) => setShowReviewDue(e.target.checked)}
                  className="calendar-toggle"
                />
              </div>
            )}
            <Button
              variant={upcomingViewMode === 'table' ? 'primary' : 'outline-primary'}
              onClick={() => setUpcomingViewMode('table')}
              className="me-2"
              size="sm"
            >
              <i className="bi bi-table me-2"></i>
              Table View
            </Button>
            <Button
              variant={upcomingViewMode === 'calendar' ? 'primary' : 'outline-primary'}
              onClick={() => setUpcomingViewMode('calendar')}
              size="sm"
            >
              <i className="bi bi-calendar-week me-2"></i>
              Calendar View
            </Button>
          </Col>
        </Row>

        {upcomingViewMode === 'calendar' ? (
          renderCalendarView()
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Job Number</th>
                <th>Project Name</th>
                <th>Model Number</th>
                <th>Mechanical Engineer</th>
                <th>Release To Scheduling</th>
                <th>Design Due<br/><small className="text-muted">(Review Due - 2 days)</small></th>
                <th>Engineering Due</th>
                <th>Days Until Review Due</th>
              </tr>
            </thead>
            <tbody>
              {upcomingDueJobs.map((job) => {
                const mechanicalDueDate = calculateMechanicalDueDate(job.MechanicalDue);
                const daysUntilDue = getDaysUntilDue(job.MechanicalDue);
                const isOverdue = isMechanicalDueDateOverdue(mechanicalDueDate);
                
                // Get day of week for Review Due date
                const reviewDueDayOfWeek = job.MechanicalDue ? (() => {
                  const [year, month, day] = job.MechanicalDue.split('-').map(num => parseInt(num, 10));
                  const date = new Date(year, month - 1, day);
                  return date.toLocaleDateString('en-US', { weekday: 'short' });
                })() : '';
                
                return (
                  <tr 
                    key={job.ID} 
                    onClick={canOpenPortalJob(job) ? () => handleRowClick(job) : undefined}
                    style={{ 
                      cursor: canOpenPortalJob(job) ? 'pointer' : 'default',
                      backgroundColor: isOverdue ? '#ffcccc' : 
                                       daysUntilDue <= 3 ? '#ffdddd' : 
                                       daysUntilDue <= 5 ? '#ffffcc' : ''
                    }}
                  >
                    <td>{job.JobNumber}</td>
                    <td>{job.ProjectName}</td>
                    <td>{job.ModelNumber}</td>
                    <td>{job.MechanicalEngineer}</td>
                    <td>{formatDate(job.ProductionRelease)}</td>
                    <td>
                      {mechanicalDueDate ? formatDate(mechanicalDueDate) : 'N/A'}
                      {isOverdue && (
                        <span className="ms-2 badge bg-danger">OVERDUE</span>
                      )}
                    </td>
                    <td>
                      {formatDate(job.MechanicalDue)}
                      {reviewDueDayOfWeek && (
                        <span className="ms-2 text-muted">({reviewDueDayOfWeek})</span>
                      )}
                    </td>
                    <td>
                      {daysUntilDue !== null ? (
                        <>
                          {daysUntilDue} day{daysUntilDue !== 1 ? 's' : ''}
                          {daysUntilDue <= 3 && 
                            <span className="ms-2 badge bg-danger">Due Soon</span>
                          }
                          {daysUntilDue > 3 && daysUntilDue <= 5 && 
                            <span className="ms-2 badge bg-warning text-dark">Due This Week</span>
                          }
                        </>
                      ) : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {!loading && upcomingDueJobs.length === 0 && 
          <Alert variant="info">No jobs with mechanical due dates in the selected period.</Alert>
        }
      </div>

      {/* Completion Modal */}
      <Modal show={showCompletionModal} onHide={handleCloseCompletionModal}>
        <Modal.Header closeButton>
          <Modal.Title>Complete Mechanical Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {completionError && <Alert variant="danger">{completionError}</Alert>}
          {completionSuccess && (
            <Alert variant="success">
              Mechanical review completed successfully!
            </Alert>
          )}
          {!completionSuccess && (
            <>
              <div className="mb-3">
                <h6>Job Details</h6>
                <p className="mb-1"><strong>Job Number:</strong> {selectedJob?.JobNumber}</p>
                <p className="mb-1"><strong>Project Name:</strong> {selectedJob?.ProjectName}</p>
                <p className="mb-1"><strong>Model Number:</strong> {selectedJob?.ModelNumber}</p>
                <p className="mb-1"><strong>Mechanical Engineer:</strong> {selectedJob?.MechanicalEngineer}</p>
                <p><strong>Review Started:</strong> {formatDate(selectedJob?.MechanicalReviewDate)}</p>
              </div>
              <Form.Group controlId="completionComment" className="mb-3">
                <Form.Label>Review Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={completionComment}
                  onChange={(e) => setCompletionComment(e.target.value)}
                  placeholder="Enter comments for the completed review..."
                />
              </Form.Group>
              <p>This will mark the mechanical review as complete and set the completion date to today.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCompletionModal} disabled={savingCompletion}>
            Close
          </Button>
          {!completionSuccess && (
            <Button 
              variant="success"
              onClick={handleConfirmCompletion} 
              disabled={savingCompletion}
            >
              {savingCompletion ? "Saving..." : "Confirm Review Complete"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Revisions Modal */}
      <Modal show={showRevisionsModal} onHide={handleCloseRevisionsModal}>
        <Modal.Header closeButton>
          <Modal.Title>Return for Revisions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {revisionsError && <Alert variant="danger">{revisionsError}</Alert>}
          {revisionsSuccess && (
            <Alert variant="success">
              Job returned for revisions successfully!
            </Alert>
          )}
          {!revisionsSuccess && (
            <>
              <div className="mb-3">
                <h6>Job Details</h6>
                <p className="mb-1"><strong>Job Number:</strong> {selectedJobForRevisions?.JobNumber}</p>
                <p className="mb-1"><strong>Project Name:</strong> {selectedJobForRevisions?.ProjectName}</p>
                <p className="mb-1"><strong>Model Number:</strong> {selectedJobForRevisions?.ModelNumber}</p>
                <p className="mb-1"><strong>Mechanical Engineer:</strong> {selectedJobForRevisions?.MechanicalEngineer}</p>
                <p><strong>Review Started:</strong> {formatDate(selectedJobForRevisions?.MechanicalReviewDate)}</p>
              </div>
              <Form.Group controlId="revisionsComment" className="mb-3">
                <Form.Label>Revisions Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={revisionsComment}
                  onChange={(e) => setRevisionsComment(e.target.value)}
                  placeholder="Enter comments for the revisions..."
                />
              </Form.Group>
              <p>This will return the job for revisions and notify the assigned mechanical engineer.</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRevisionsModal} disabled={savingRevisions}>
            Close
          </Button>
          {!revisionsSuccess && (
            <Button 
              variant="warning"
              onClick={handleConfirmReturnForRevisions} 
              disabled={savingRevisions}
            >
              {savingRevisions ? "Saving..." : "Confirm Return for Revisions"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MechanicalReview;
